// Bridge zone — projects, dashboard, activity, admiral, squadronAdmin, users
import type {
  User, Project, ProjectMember, DashboardData, ActivityItem,
  Squadron, SquadronMember,
} from './types'
import type { RequestHelper } from './client'

export function bridgeMethods(r: RequestHelper) {
  return {
    // Auth (Keel-adjacent but consumed via Bridge)
    auth: {
      me: () => r.get<{ user: User }>('/auth/me'),
      logout: () => r.post<void>('/auth/logout'),
    },

    // Dashboard
    dashboard: {
      get: () => r.get<DashboardData>('/hub/dashboard'),
      my: () => r.get<DashboardData>('/hub/my-dashboard'),
    },

    // Projects
    projects: {
      list: () => r.get<{ projects: Project[] }>('/hub/projects'),
      get: (id: string) => r.get<{ project: Project }>(`/hub/projects/${id}`),
      create: (data: Partial<Project>) => r.post<{ project: Project }>('/hub/projects', data),
      update: (id: string, data: Partial<Project>) => r.put<{ project: Project }>(`/hub/projects/${id}`, data),
      delete: (id: string) => r.del<void>(`/hub/projects/${id}`),
      members: {
        list: (projectId: string) => r.get<{ members: ProjectMember[] }>(`/hub/projects/${projectId}/members`),
        add: (projectId: string, data: { email: string; role: string }) => r.post<{ member: ProjectMember }>(`/hub/projects/${projectId}/members`, data),
        remove: (projectId: string, userId: string) => r.del<void>(`/hub/projects/${projectId}/members/${userId}`),
        updateRole: (projectId: string, userId: string, role: string) => r.put<void>(`/hub/projects/${projectId}/members/${userId}/role`, { role }),
      },
      activity: (projectId: string, limit = 30) => r.get<{ activity: ActivityItem[] }>(`/hub/projects/${projectId}/activity?limit=${limit}`),
    },

    // Activity
    activity: {
      feed: (limit = 50) => r.get<{ activity: ActivityItem[] }>(`/hub/activity?limit=${limit}`),
      mine: (limit = 30) => r.get<{ activity: ActivityItem[] }>(`/hub/activity/mine?limit=${limit}`),
    },

    // Users (admin)
    users: {
      list: (params?: Record<string, string>) => {
        const query = params ? `?${new URLSearchParams(params).toString()}` : ''
        return r.get<{ users: User[] }>(`/hub/users${query}`)
      },
      get: (id: string) => r.get<{ user: User }>(`/hub/users/${id}`),
      updateRole: (id: string, role: string) => r.put<void>(`/hub/users/${id}/role`, { role }),
      stats: () => r.get<Record<string, number>>('/hub/users/stats/overview'),
    },

    // Admiral / Squadrons
    admiral: {
      mySquadrons: () => r.get<{ squadrons: Squadron[] }>('/hub/admiral/squadrons'),
      squadron: (id: string) => r.get<{ squadron: Squadron }>(`/hub/admiral/squadrons/${id}`),
      projects: (squadronId: string) => r.get<{ projects: Project[] }>(`/hub/admiral/squadrons/${squadronId}/projects`),
      crew: (squadronId: string) => r.get<{ members: SquadronMember[] }>(`/hub/admiral/squadrons/${squadronId}/crew`),
      recruitment: (squadronId: string) => r.get<Record<string, unknown>>(`/hub/admiral/squadrons/${squadronId}/recruitment`),
      activity: (squadronId: string) => r.get<{ activity: ActivityItem[] }>(`/hub/admiral/squadrons/${squadronId}/activity`),
      transfer: (squadronId: string, data: { user_id: string; target_squadron_id: string }) => r.post<void>(`/hub/admiral/squadrons/${squadronId}/transfer`, data),
      assignCaptain: (squadronId: string, projectId: string, data: { user_id: string }) => r.post<void>(`/hub/admiral/squadrons/${squadronId}/captain/${projectId}`, data),
    },

    // Squadron Management (Fleet Admiral)
    squadronAdmin: {
      list: () => r.get<{ squadrons: Squadron[] }>('/hub/admin/squadrons'),
      create: (data: { name: string; description?: string; admiral_id: string }) => r.post<{ squadron: Squadron }>('/hub/admin/squadrons', data),
      update: (id: string, data: Partial<Squadron>) => r.put<{ squadron: Squadron }>(`/hub/admin/squadrons/${id}`, data),
      delete: (id: string) => r.del<void>(`/hub/admin/squadrons/${id}`),
      addMember: (squadronId: string, data: { user_id: string; role?: string }) => r.post<void>(`/hub/admin/squadrons/${squadronId}/members`, data),
      removeMember: (squadronId: string, userId: string) => r.del<void>(`/hub/admin/squadrons/${squadronId}/members/${userId}`),
      addProject: (squadronId: string, data: { project_id: string }) => r.post<void>(`/hub/admin/squadrons/${squadronId}/projects`, data),
      removeProject: (squadronId: string, projectId: string) => r.del<void>(`/hub/admin/squadrons/${squadronId}/projects/${projectId}`),
    },
  }
}
